/**
 * Painel de funcionalidades — tudo nasce ligado; desligar é a ação, e ela
 * avisa o grupo pelo sino. Só quem tem `manage_trip_settings` mexe.
 */
import { featureEnabled, TRIP_FEATURE_LABELS, TRIP_FEATURES, type TripFeature } from '@nv/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, setTripFeature } from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { useTrip } from '../../trip/TripContext';
import { guardarViagemAtiva } from '../../trip/viagemAtiva';
import { Card } from '../../ui/primitives';

export function FuncionalidadesDaViagem({ podeEditar }: { podeEditar: boolean }) {
  const { trip } = useTrip();
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [mexendo, setMexendo] = useState<TripFeature | null>(null);

  async function alternar(f: TripFeature, ligar: boolean) {
    setMexendo(f);
    setErro(null);
    try {
      const { features } = await setTripFeature(trip.id, f, ligar);
      guardarViagemAtiva(userId, { ...trip, features });
      await cliente.invalidateQueries({ queryKey: ['viagens'] });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar agora.');
    } finally {
      setMexendo(null);
    }
  }

  return (
    <ListaDeFuncionalidades
      ligadas={(f) => featureEnabled(trip.features, f)}
      onAlternar={(f, ligar) => void alternar(f, ligar)}
      desabilitada={(f) => !podeEditar || mexendo === f}
      erro={erro}
    />
  );
}

/**
 * A lista em si (sem rede): Configurações liga/desliga no servidor na hora;
 * Criar viagem guarda a escolha e aplica depois que a viagem existe.
 */
export function ListaDeFuncionalidades({
  ligadas,
  onAlternar,
  desabilitada,
  erro,
  nota = 'Tudo vem ligado. Desligar avisa o grupo pelo sino.',
}: {
  ligadas: (f: TripFeature) => boolean;
  onAlternar: (f: TripFeature, ligar: boolean) => void;
  desabilitada?: (f: TripFeature) => boolean;
  erro?: string | null;
  nota?: string;
}) {
  return (
    <Card>
      <p className="auth-note" style={{ marginTop: 0 }}>
        {nota}
      </p>
      {TRIP_FEATURES.map((f) => {
        const ligada = ligadas(f);
        return (
          <div className="alertas__linha" key={f} style={{ padding: '8px 0' }}>
            <span className="mapa__pessoa-texto">
              <span className="mapa__pessoa-nome">{TRIP_FEATURE_LABELS[f].nome}</span>
              <span className="mapa__pessoa-familia">{TRIP_FEATURE_LABELS[f].descricao}</span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={ligada}
              aria-label={TRIP_FEATURE_LABELS[f].nome}
              className={`mapa__toggle${ligada ? ' mapa__toggle--on' : ''}`}
              disabled={desabilitada?.(f) ?? false}
              onClick={() => onAlternar(f, !ligada)}
            >
              <span className="mapa__toggle-bola" />
            </button>
          </div>
        );
      })}
      {erro && <p className="auth-error" role="alert">{erro}</p>}
    </Card>
  );
}
